import { useQueries } from '@tanstack/react-query';

import { transitionRequestQuery, useRules } from '@/features/rules';
import type { TransitionRequest } from '@/features/rules';
import type { Rule } from '@/shared/api/generated/rules';

import { sourceStateFor } from '../model/notifications';
import type { Access, SourceState } from '../model/notifications';

/* The approval half of the feed, and the only place in this feature that fetches anything.
 *
 * **There is no endpoint for "requests waiting for me".** `GET /v1/rules/{id}/transition-requests` is
 * per rule and answers `{ request: null }` for the ordinary case, so a queue is N of those — one per
 * *draft* rule, since `TransitionApproval` and the kernel both agree that only a draft has an
 * activation to govern. That fan-out is the honest way to build this from the API that exists, and
 * bounding it is the honest way to ship the fan-out.
 *
 * Nothing here re-implements the fetch. `transitionRequestQuery` is the rules feature's own key and
 * fetcher, so a reader with a rule's page open has already paid for that rule's lookup and this costs
 * them nothing — and there is exactly one answer to what the envelope means.
 */

/** How many draft rules to look up per pass.
 *
 * **A bound, and a reported one.** Twenty-five requests from the app chrome is already the largest
 * thing the header does; an estate with three hundred draft rules must not make the bell issue three
 * hundred. What makes the bound acceptable is `notLookedAt` — the count travels out of here and onto
 * the panel, so a reader learns the answer is a floor rather than being shown a smaller number as if
 * it were the total. `PageMeta.truncated` and `DatasetNames.truncated` are the same decision.
 *
 * **The cost is real and is worth stating rather than absorbing.** Twenty-six requests per cold page
 * load, from a component mounted on every screen. `APPROVAL_STALE_MS` keeps a navigation free and a
 * hard reload does not, and the edge's read bucket is deployment-wide — so on a large estate this is
 * the largest single consumer of it. The right fix is a route that answers "requests waiting for me"
 * in one read, which would make the bound and this whole fan-out unnecessary; that is a server change
 * and is recorded as one rather than approximated here.
 *
 * Ordering is the listing's, which is the server's — so the *set* looked at is stable between passes
 * rather than a different twenty-five each time. It is deliberately not "the oldest twenty-five
 * drafts": the listing does not sort by request age, no route does, and picking a subset by a
 * property this client cannot see would be a claim rather than a bound.
 */
export const MAX_APPROVAL_LOOKUPS = 25;

/** How long a lookup stays fresh for the bell.
 *
 * The rule's own approval panel wants the current answer the instant a signature lands, and gets it —
 * `useTransitionRequest` sets no `staleTime` and the mutation invalidates the key. The header wants
 * the opposite: it is mounted for the whole session and must not re-ask twenty-five questions because
 * somebody navigated. Sixty seconds is the same figure `useIdentity` chose for the same reason, and
 * the shared key means a signature made in this tab still invalidates it immediately.
 */
const APPROVAL_STALE_MS = 60_000;

export interface ApprovalLookup {
  readonly rule: Rule;
  readonly request: TransitionRequest;
}

export interface ApprovalFeed {
  readonly lookups: readonly ApprovalLookup[];
  readonly state: SourceState;
  /** Draft rules past `MAX_APPROVAL_LOOKUPS`. Never silently dropped. */
  readonly notLookedAt: number;
  /** Lookups that failed. A failed lookup is a blind spot, **not** the absence of a request. */
  readonly failed: number;
}

/** Every draft rule's open activation request, bounded and reported.
 *
 * `access` is a permission answer rather than a lazy-load: without `read:rules.rule` this asks nothing
 * and reports `forbidden`, so an ordinary boundary does not arrive as a failed read. See
 * `RulesQueryOptions`. It is a four-way answer rather than a boolean because "we have not read the
 * caller's permissions yet" is not "you may not" — see `Access`.
 */
export function useApprovalFeed(access: Access): ApprovalFeed {
  const rules = useRules('', { enabled: access === 'permitted' });

  /* Draft only. A published or deprecated rule has no activation to govern — `TransitionApproval`
   * returns null for one and the approval it once had is spent (migration 0042) — so looking one up
   * would spend a slot of the bound on a guaranteed `{ request: null }`. */
  const drafts = rules.isSuccess
    ? rules.data.items.filter((rule) => rule.state === 'draft')
    : [];
  const lookedAt = drafts.slice(0, MAX_APPROVAL_LOOKUPS);

  /* One hook whatever the length, which is what `useQueries` is for — the alternative is a hook in a
   * loop, which React forbids and `react-hooks` catches. */
  const results = useQueries({
    queries: lookedAt.map((rule) => ({
      ...transitionRequestQuery(rule.id),
      retry: false,
      staleTime: APPROVAL_STALE_MS,
    })),
  });

  const lookups: ApprovalLookup[] = [];
  let failed = 0;
  let reading = false;
  for (const [index, result] of results.entries()) {
    const rule = lookedAt[index];
    /* `noUncheckedIndexedAccess` makes this `Rule | undefined`, and the guard is not ceremony: the
     * two arrays are built from one source in one render, and a mismatch would pair a request with
     * the wrong rule's name — a row naming the wrong rule is worse than a row that is absent. */
    if (rule === undefined) continue;
    if (result.isPending) {
      reading = true;
      continue;
    }
    if (result.isError) {
      failed += 1;
      continue;
    }
    /* `null` is the ordinary answer: the rule exists and nobody has asked. Not a gap. */
    if (result.data === null) continue;
    lookups.push({ rule, request: result.data });
  }

  return {
    lookups,
    /* The listing and the fan-out are one source to a reader: a page of rules we could not read and a
     * lookup still in flight both mean "this answer is not final yet", and splitting them would put
     * two lines in the panel about one question. `reading` folds in the per-rule pending flag for
     * that reason — `failed` is the half that is genuinely a different sentence, and it travels
     * separately. */
    state: sourceStateFor(access, {
      isError: rules.isError,
      isPending: rules.isPending || reading,
    }),
    notLookedAt: drafts.length - lookedAt.length,
    failed,
  };
}
